/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {AxiosError, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';
import {state} from '@/store/modules/user-session';
import ApiException from '@/exceptions/ApiException';
import {AbstractHttpService} from '@/services/http/abstract';
import store from '@/store';
import {PkceRefreshRequest, PkceRefreshRequestInterface} from '@/services/pkce/types';
import router from '@/router';
import CorsException from '@/exceptions/CorsException';
import languageService from '@/services/language';

/**
 * Api service class used to leverage http library in order to communicate with api.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export class ApiService extends AbstractHttpService {
    /**
     *  Constructor.
     *
     *  This includes the configuration of the following plugins:
     *  1. Throttle adapter - ensures that a route is not flooded with requests during a pre-defined time period.
     *  2. Cache adapter - caches requests. Important - this should be deactivated on some routes: {cache: false}.
     *  3. Retry plugin - attempts retries on routes that fail.
     */
    constructor() {
        super({
            accept: 'application/ld+json',
            contentType: 'application/json',
            cache: {enabledByDefault: false},
            baseURL: process.env.VUE_APP_API || '',
            throttle: {
                threshold: 5000,
            },
        });
    }

    /**
     * Get new token from refresh token.
     *
     * @return {Promise<boolean>}
     */
    public async getRefreshToken(): Promise<boolean> {
        const params: PkceRefreshRequestInterface = new PkceRefreshRequest(
            store.getters['userSessionModule/refreshToken'],
            process.env.VUE_APP_CLIENTID || '',
        );

        try {
            await store.dispatch('userSessionModule/refresh', params);
            return true;
        } catch (exception) {
            return false;
        }
    }

    /**
     * Add request interceptors.
     *
     * This method can be overridden in inheriting classes - as can the default request interceptors that it registers.
     *
     * @param {AxiosInterceptorManager<AxiosRequestConfig>} request
     */
    public addRequestInterceptors(request: AxiosInterceptorManager<AxiosRequestConfig>): void {
        super.addRequestInterceptors(request);
        request.use(this.applyAuthorisationToken);
        request.use(this.applyLanguageHeader);
    }

    /**
     * Apply authorisation token to request if available.
     *
     * @param {AxiosRequestConfig} config
     *
     * @returns {AxiosRequestConfig}
     */
    public applyAuthorisationToken(config: AxiosRequestConfig): AxiosRequestConfig {
        // clear non-standard params
        if (undefined !== config.params && config.params.skipAuthorization) {
            config.params.skipAuthorization = undefined;
            return config;
        }

        if ('' !== state.session.token &&
            state.session.expiry > (new Date().getDate())) {
            config.headers.Authorization = `Bearer ${state.session.token}`;
        }

        return config;
    }

    /**
     * Apply the language header if applicable.
     *
     * @param {AxiosRequestConfig} config
     * @return {AxiosRequestConfig}
     */
    public applyLanguageHeader(config: AxiosRequestConfig): AxiosRequestConfig {
        config.headers['Accept-Language'] = languageService.getSelectedLocale() || 'en';
        config.headers['X-Custom-Language'] = languageService.getSelectedLocale() || 'en';

        return config;
    }

    /**
     * Global success response handler
     *
     * @override
     *
     * @param {AxiosResponse} response
     * @returns {AxiosResponse}
     */
    public handleSuccess(response: AxiosResponse): AxiosResponse {
        // Custom response handling
        return response;
    }

    /**
     * Inheritable exception handler.
     *
     * @override
     *
     * @param {ApiException} exception
     * @return {Promise<ApiException>}
     */
    public async handleException(exception: ApiException): Promise<never> {
        // validate the response status and auto redirect if unauthorized is found
        switch (exception.status) {
            // if we receive a 401 here then retries have failed and we need to actively login
            case 401:
                // if we are currently on the login page then ignore
                if ('login' !== router.currentRoute.name) {
                    router.push({name: 'login'});
                }
                break;
        }

        return Promise.reject(exception);
    }

    /**
     * Global error response handler
     * @param error
     * @returns {Promise<any>}
     */
    public async handleError(error: AxiosError): Promise<any> {
        // typically if there is an undefined response then it is a CORS issue
        if (undefined === error.response) {
            return Promise.reject(new CorsException(error));
        }

        if (!error.response) {
            return Promise.reject(error);
        }

        const retryConfig: AxiosRequestConfig & {__isRetryRequest?: boolean} = error.config;
        if (401 === error.response.status && retryConfig && !retryConfig.__isRetryRequest) {
            retryConfig.__isRetryRequest = true;
            if (!await this.getRefreshToken()) {
                store.dispatch('userSessionModule/logout');
                delete error.config.headers['Authorization'];
                return this.axios.request(error.config);
            }

            // retry the original request
            return this.axios.request(error.config);
        }

        const exception: ApiException = new ApiException(
            error.response.status,
            error.response.data,
        );

        // validate the response status and auto redirect if unauthorized is found
        return this.handleException(exception);
    }
}
